import { useEffect, useState } from 'react';
import { CheckCircleIcon, ClockIcon, MagnifyingGlassIcon, MinusCircleIcon, ShieldExclamationIcon, UserMinusIcon, UserPlusIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { OverlayGrayBackground } from '../OverlayGrayBackground';
import { getAuditInfo, getUsersToSelect, UsersManagementLoadingKeys } from '../../slices/managementSlices/usersSettingsSlice';
import { useDispatch, useSelector } from 'react-redux';
import { SelectDropdown } from '../SelectDropdown';
import { useDebounce } from '../../helpers/useDebounce';
import { UserCheck, UserX } from 'lucide-react';

const ChangesTable = ({ changes }) => {
  const keys = Object.keys(changes);

  const formatValue = (value) => {
    if (value === null || value === undefined) return "";
    if (typeof value === "object") {
      return (
        <pre style={{ margin: 0, display: "inline" }}>
          {JSON.stringify(value, null, 2)}
        </pre>
      );
    }
    return String(value);
  };

  // Helpers to diff Permissions arrays and render as badges
  const arrayToPermissionsMap = (arr = []) => {
    const result = {};
    (arr || []).forEach((categoryObj) => {
      if (!categoryObj || typeof categoryObj !== "object") return;
      Object.entries(categoryObj).forEach(([category, pages]) => {
        if (!result[category]) result[category] = {};
        (pages || []).forEach((pageObj) => {
          if (!pageObj || typeof pageObj !== "object") return;
          Object.entries(pageObj).forEach(([page, level]) => {
            result[category][page] = level;
          });
        });
      });
    });
    return result;
  };

  const computePermissionsDiff = (oldPermissions, newPermissions) => {
    const oldMap = arrayToPermissionsMap(oldPermissions);
    const newMap = arrayToPermissionsMap(newPermissions);
    const diffs = [];

    const categories = new Set([
      ...Object.keys(oldMap || {}),
      ...Object.keys(newMap || {}),
    ]);

    categories.forEach((category) => {
      const oldPages = oldMap[category] || {};
      const newPages = newMap[category] || {};
      const pages = new Set([
        ...Object.keys(oldPages || {}),
        ...Object.keys(newPages || {}),
      ]);
      pages.forEach((page) => {
        const oldValue = oldPages[page];
        const newValue = newPages[page];
        if (oldValue !== newValue) {
          diffs.push({ category, page, old: oldValue, new: newValue });
        }
      });
    });
    return diffs;
  };

  const renderPermissionsDiff = (oldValue, newValue) => {
    const diffs = computePermissionsDiff(oldValue, newValue);
    if (!diffs.length) {
      return (
        <span className='text-xs text-gray-700/60'>No permission changes</span>
      );
    }
    return (
      <div className='flex flex-wrap gap-1'>
        {diffs.map((d, idx) => {
          const isAdded = d.old === undefined && d.new !== undefined;
          const isRemoved = d.old !== undefined && d.new === undefined;
          const badgeClass = isAdded
            ? "bg-green-50 text-green-700 border-green-200"
            : isRemoved
            ? "bg-red-50 text-red-700 border-red-200"
            : "bg-blue-50 text-blue-700 border-blue-200";
          return (
            <span
              key={`${d.category}-${d.page}-${idx}`}
              className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs ${badgeClass}`}
            >
              {`${d.category} / ${d.page}: ${d.old ?? "—"} → ${d.new ?? "—"}`}
            </span>
          );
        })}
      </div>
    );
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
      {keys.map((key) => {
        const oldValue = changes[key]?.old;
        const newValue = changes[key]?.new;
        if (/password/i.test(String(key))) {
          const mask = (v) => {
            const len = String(v ?? "").length;
            return "*".repeat(len || 8);
          };
          return (
            <div key={key} style={{ fontSize: 12 }}>
              <span style={{ fontWeight: 600 }}>{key}: </span>
              <span>{mask(oldValue)}</span>
              <span> </span>
              <span style={{ opacity: 0.7 }}>→</span>
              <span> </span>
              <span>{mask(newValue)}</span>
            </div>
          );
        }
        if (key === "Permissions") {
          return (
            <div key={key} style={{ fontSize: 12 }}>
              <span style={{ fontWeight: 600 }}>{key}: </span>
              {renderPermissionsDiff(oldValue, newValue)}
            </div>
          );
        }
        return (
          <div key={key} style={{ fontSize: 12 }}>
            <span style={{ fontWeight: 600 }}>{key}: </span>
            <span>{formatValue(oldValue)}</span>
            <span> </span>
            <span style={{ opacity: 0.7 }}>→</span>
            <span> </span>
            <span>{formatValue(newValue)}</span>
          </div>
        );
      })}
    </div>
  );
};

const getIconByAudit = (item) => {
    switch(item.type) {
        case ("removed_user"): {
            return <UserMinusIcon className='h-5 w-5 text-red-600' />
        }
        case ("disabled_user"): {
            return <UserX className='h-5 w-5 text-red-600' />
        }
        case ("enabled_user"): {
            return <UserCheck className='h-5 w-5 text-green-600' />
        }
        case ("added_user"): {
            return <UserPlusIcon className='h-5 w-5 text-green-600' />
        }
        case ("removed_role"): {
            return <MinusCircleIcon className='h-5 w-5 text-red-600' />
        }
        case ("added_role"): {
            return <CheckCircleIcon className='h-5 w-5 text-green-600' />
        }
        default:
            return <ShieldExclamationIcon className='h-5 w-5 text-blue-600' />
    }
}

const getDetailsByAudit = (item) => {
    switch(item.type) {
        case ("disabled_user"): {
            return item.reason ? (
                <p className='text-xs text-gray-700/60 italic'>
                    {item.reason}
                </p>
            ) : null
        }
        case ("changed_user"): {
            return (
                <ChangesTable changes={item?.changedValues || {}} />
            )
        }
        case ("changed_role"): {
            return (
                <ChangesTable changes={item?.changedValues || {}} />
            )
        }
        default:
            return null;
    }
}

export const AuditDrawer = ({ 
    isOpen, 
    onClose, 
}) => {

    const dispatch = useDispatch();
    const [action, setAction] = useState("all_actions");
    const [user, setUser] = useState("all_users");
    const [search, setSearch] = useState("");
    const currentSearchValue = useDebounce(search, 500);
    
    const {
        auditInfo,
        usersToSelect,
        loadingKeys: usersLoadingKeys,
        loading: usersLoading
    } = useSelector((state) => state.usersSettings);

    useEffect(() => {
        const handleKeyDown = (event) => {
            if (event.key === 'Escape') {
                onClose();
            }
        };
        if (!isOpen) {
            setAction(null);
            setUser(null);
            setSearch(null);
        }
        if (isOpen) {   
            document.body.style.overflow = 'hidden';
            window.addEventListener('keydown', handleKeyDown);
        }
        return () => {
            document.body.style.overflow = 'auto';
            window.removeEventListener('keydown', handleKeyDown);
        };
    }, [isOpen, onClose]);

    useEffect(() => {
        if (isOpen) {
            dispatch(getUsersToSelect({}));
            dispatch(getAuditInfo({}));
        }
    }, [isOpen, dispatch]);
    useEffect(() => {
        if (isOpen) {
            dispatch(getAuditInfo({
                user,
                action,
                search: currentSearchValue
            }))
        }
    }, [action, user, currentSearchValue, isOpen, dispatch]);

    const usersToSelectIsLoading = () => usersLoading && usersLoadingKeys.includes(UsersManagementLoadingKeys.getUsersToSelect);
    const auditIsLoading = () => usersLoading && usersLoadingKeys.includes(UsersManagementLoadingKeys.getAuditInfo);

    if (!isOpen) return null;

    return (
        <>
            <div
                className={`fixed inset-y-0 right-0 w-full max-w-xl bg-white shadow-xl transform transition-transform duration-300 ease-in-out z-50 flex flex-col`}
            >
                <div className='p-6 pb-2 border-border border-b'>
                    <div className='flex flex-col mb-0 gap-4'>
                        <div className='flex items-center justify-between'>
                            <div className='flex flex-row gap-2 items-center'>
                                <ClockIcon className="h-5 w-5 text-primary" />
                                <h2 className="text-lg font-semibold">
                                    Audit Trail
                                </h2>
                            </div>
                            <button 
                                onClick={onClose}
                                className='inline-flex items-center justify-center whitespace-nowrap text-sm font-medium transition-all disabled:pointer-events-none disabled:opacity-50 h-8 rounded-md gap-1.5 px-3'
                            >
                                <XMarkIcon className="h-5 w-5" />
                            </button>
                        </div>
                        <div className='space-y-4'>
                            <div className="relative flex-1">
                                <MagnifyingGlassIcon className="lucide lucide-search absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground"/>
                                <input
                                    placeholder="Search audit log..."
                                    value={search}
                                    onChange={(e) => setSearch(e.target.value)}
                                    className="border-input flex h-9 w-full min-w-0 rounded-md border bg-transparent px-3 py-1 text-base shadow-xs outline-none disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50 md:text-sm focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] pl-10"
                                />
                            </div>
                            <div className='flex gap-2'>
                                <div className="w-full">
                                    <SelectDropdown
                                        options={[
                                            { label: "All Actions", value: "all_actions" },
                                            { label: "User Removed", value: "removed_user" },
                                            { label: "User Disabled", value: "disabled_user" },
                                            { label: "User Enabled", value: "enabled_user" },
                                            { label: "User Changed", value: "changed_user" },
                                            { label: "User Added", value: "added_user" },
                                            { label: "Role Removed", value: "removed_role" },
                                            { label: "Role Added", value: "added_role" },
                                            { label: "Role Changed", value: "changed_role" },
                                        ]}
                                        value={action}
                                        onChange={setAction}
                                        selectClassname="bg-white py-1.5"
                                    />
                                </div>
                                <div className="w-full">
                                    <SelectDropdown
                                        options={[
                                            { label: "All Users", value: "all_users" },
                                            ...(usersToSelect || [])
                                        ]}
                                        value={user}
                                        onChange={setUser}
                                        disabled={usersToSelectIsLoading()}
                                        selectClassname="bg-white py-1.5"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div className='flex-1 overflow-y-auto overflow-x-hidden relative'>
                    {auditIsLoading() && <OverlayGrayBackground />}
                    <div className='p-6 flex flex-col gap-4'>
                        {(auditInfo || []).map((audit) => {
                            return (
                                <article key={audit.id} className='bg-card text-primary flex flex-col gap-6 rounded-xl border shadow-sm p-3'>
                                    <div className='flex items-start gap-3'>
                                        <div>
                                            {getIconByAudit(audit)}
                                        </div>
                                        <div className='flex-1'>
                                            <p className='text-sm font-medium text-primary mb-2'>
                                                {audit.title || ""}
                                            </p>
                                            <div className='flex items-center gap-2 mb-2'>
                                                <span className='relative flex size-8 shrink-0 overflow-hidden rounded-full h-6 w-6'>
                                                    <span className='bg-gray-100 flex size-full items-center justify-center rounded-full text-xs'>
                                                        {(audit?.admin || "")
                                                            .split(" ")
                                                            .map(word => word[0] || "")
                                                            .join("")
                                                            .toUpperCase()}
                                                    </span>
                                                </span>
                                                <span className='text-xs text-gray-700/60'>
                                                    {audit?.admin || ""}
                                                </span>
                                                {(audit?.role || audit?.user) && (
                                                    <>
                                                        <span className='text-xs text-gray-700/60'>
                                                            →
                                                        </span>
                                                        <span className='text-xs text-gray-700/60'>
                                                            {audit?.role || audit?.user}
                                                        </span>
                                                    </>
                                                )}
                                            </div>
                                            <div className='space-y-1'>
                                                {getDetailsByAudit(audit)}
                                            </div>
                                            <p className='text-xs text-gray-700/60 mt-2'>
                                                {audit.changedDate || ""}
                                            </p>
                                        </div>
                                    </div>
                                </article>
                            )
                        })}
                    </div>
                </div>
                <div className='p-4 border-t border-border'>
                    <p className='text-xs text-muted-foreground text-center'>
                        Showing last 50 permission changes
                    </p>
                </div>
            </div>
        </>
    );
};